import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, ChevronRight, Calendar, MessageCircle, Phone, GraduationCap,
  Scissors, Heart, Shield, Users, MapPin, Building2, Microscope, Sparkles,
  Target, Brain, HandHeart, Stethoscope, Award, Activity
} from "lucide-react";

const expertise = {
  cancers: [
    "Ovarian Cancer (Early & Advanced Stage)",
    "Cervical Cancer",
    "Uterine (Endometrial) Cancer",
    "Vulval & Vaginal Cancer",
    "Rare Gynecologic Malignancies",
  ],
  surgical: [
    "Major Cytoreductive (Debulking) Surgeries",
    "HIPEC (Hyperthermic Intraperitoneal Chemotherapy)",
    "Laparoscopic Cancer Surgery",
    "Robotic-Assisted Gynecologic Surgery",
    "Pelvic Exenteration (Complex Cancer Surgery)",
  ],
  benign: [
    "Endometriosis (Deep Infiltrating Endometriosis)",
    "Ovarian Cysts",
    "Uterine Fibroids (Tumors)",
  ],
  preventive: [
    "Cancer Screening (Pap Smear, HPV Testing)",
    "High-Risk Cancer Evaluation",
    "Preventive Oncology & Early Detection Programs",
  ],
};

const clinicalPrinciples = [
  { icon: Target, text: "Early and accurate diagnosis" },
  { icon: Brain, text: "Evidence-based treatment planning" },
  { icon: Scissors, text: "Minimally invasive surgery whenever possible" },
  { icon: Heart, text: "Organ and fertility preservation (when safe and appropriate)" },
  { icon: Users, text: "Multidisciplinary cancer care coordination" },
  { icon: HandHeart, text: "Compassionate emotional support throughout treatment" },
];

const hospitals = [
  "Manipal Hospitals, EM Bypass (formerly Medica Superspecialty Hospital)",
  "Manipal Hospitals, Mukundapur",
  "Manipal Hospitals, Broadway",
  "Manipal Hospitals, Saltlake",
];

const philosophyPoints = [
  "Clear communication and patient education",
  "Transparent treatment planning",
  "Emotional support for patients and families",
  "Focus on quality of life and long-term outcomes",
];

const patientExperience = [
  "Clear and easy to understand medical explanations",
  "Supportive and empathetic consultation experience",
  "Structured and well-planned treatment pathways",
  "High level of surgical confidence and expertise",
];

const areasServed = [
  "Mukundapur", "EM Bypass", "Salt Lake", "Phoolbagan",
  "Kankurgachi", "Garia", "Kasba", "Ballygunge", "South Kolkata",
];

const stats = [
  { value: "10+", label: "Years of Experience" },
  { value: "1000+", label: "Complex Cancer Cases" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>
          Dr. Neha Agarwal – Gynecologic Oncologist in Kolkata | Cancer & Robotic Surgeon
        </title>

        <meta
          name="description"
          content="Dr. Neha Agarwal is a leading gynecologic oncologist in Kolkata specializing in ovarian, cervical, uterine cancer, HIPEC, robotic and laparoscopic cancer surgery."
        />
      </Helmet>
      <div className="bg-[#F9F9F6] min-h-screen">


        {/* ─── HERO ─── */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="about-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.nav
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
              data-testid="about-breadcrumb"
            >
              <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#1E3F33] font-medium">About</span>
            </motion.nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="lg:col-span-7"
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  About the Doctor
                </p>
                {/* <h1
                  data-testid="about-page-title"
                  className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
                >
                  Dr. Neha Agarwal
                </h1>
                <p className="text-lg sm:text-xl font-medium text-[#8FA998] mb-6">
                  Gynecologic Oncologist & Robotic Surgeon, Kolkata
                </p> */}
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-6" data-testid="about-intro-text">
                  Welcome to the practice of Dr. Neha Agarwal, a leading Gynecologic Oncologist in Kolkata, dedicated to the diagnosis, treatment, and long-term care of women with gynecologic cancers and complex gynecologic conditions.
                </p>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-8">
                  With advanced training in oncology and extensive surgical experience, she provides comprehensive care for ovarian cancer, cervical cancer, uterine cancer, vulval cancer, and rare gynecologic malignancies, along with expertise in HIPEC, laparoscopic surgery, and robotic-assisted cancer surgery.
                </p>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-8">
                  Her approach is centered on precision treatment, evidence-based medicine, and compassionate patient care, ensuring that every patient receives individualized attention at every stage of treatment.              </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/book-appointment"
                    data-testid="about-hero-book-btn"
                    className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    Book Appointment
                  </Link>
                  <a
                    href="https://wa.me/916291000829"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="about-hero-whatsapp-btn"
                    className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              </motion.div>

              {/* Doctor Image + Credentials */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="lg:col-span-5"
              >
                <div className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] overflow-hidden">
                  <div className="aspect-[3/4] bg-[#E6E7E2] relative">
                    <img
                      src="images/profile.jpeg"
                      alt="Dr. Neha Agarwal - Gynecologic Oncologist"
                      data-testid="about-doctor-image"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6" data-testid="about-credentials-summary">
                    <h3 className="font-serif text-xl text-[#1E3F33] mb-2">Dr. Neha Agarwal</h3>
                    <p className="text-sm text-[#4A6359] mb-3">MBBS, DNB Obstetrics & Gynaecology (NBEMS) , DrNB Gynaecologic Oncology (Tata Medical Center)</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-[#1E3F33]/10 text-[#1E3F33] px-3 py-1 rounded-full font-medium">Gynecologic Oncology</span>
                      <span className="text-xs bg-[#1E3F33]/10 text-[#1E3F33] px-3 py-1 rounded-full font-medium">HIPEC</span>
                      <span className="text-xs bg-[#1E3F33]/10 text-[#1E3F33] px-3 py-1 rounded-full font-medium">Robotic Surgery</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── STATS ─── */}
        <section className="py-12 bg-[#F2EFE9]" data-testid="about-stats-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-2 gap-4 md:gap-8">
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`about-stat-${i}`}
                  className="text-center py-6"
                >
                  <p className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3F33] mb-1">
                    {stat.value}
                  </p>

                  <p className="text-sm sm:text-base text-[#4A6359] font-medium">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── ABOUT BACKGROUND ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="about-background-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="max-w-3xl"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Background & Training
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight mb-6" data-testid="about-background-heading">
                About Dr. Neha Agarwal
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-5">
                Dr. Neha Agarwal is a highly trained Gynecologic Oncologist and Robotic Surgeon based in Kolkata with over a decade of experience in women's cancer care.
              </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-5">
                She completed her foundational medical education with MBBS and DNB in Obstetrics and Gynecology, followed by advanced super-specialty training in Gynecologic Oncology from Tata Medical Center, one of India's leading cancer institutes.
              </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed">
                During her training and professional journey, she has been involved in the management of thousands of complex cancer cases and has developed expertise in advanced surgical oncology procedures.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ─── AREAS OF EXPERTISE ─── */}
        <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="about-expertise-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-14"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Specializations
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="about-expertise-heading">
                Areas of Expertise
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Gynecologic Cancers */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="bg-[#F9F9F6] rounded-2xl md:rounded-[2rem] p-7 md:p-8"
                data-testid="about-expertise-cancers"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <Microscope className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33]">Gynecologic Cancers</h3>
                </div>
                <ul className="space-y-3">
                  {expertise.cancers.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-[#1E3F33] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Surgical Expertise */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="bg-[#F9F9F6] rounded-2xl md:rounded-[2rem] p-7 md:p-8"
                data-testid="about-expertise-surgical"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <Scissors className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33]">Advanced Surgical Expertise</h3>
                </div>
                <ul className="space-y-3">
                  {expertise.surgical.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-[#1E3F33] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Benign Conditions */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="bg-[#F9F9F6] rounded-2xl md:rounded-[2rem] p-7 md:p-8"
                data-testid="about-expertise-benign"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33]">Benign & Pre-cancer Conditions</h3>
                </div>
                <ul className="space-y-3">
                  {expertise.benign.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-[#1E3F33] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Preventive */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="bg-[#F9F9F6] rounded-2xl md:rounded-[2rem] p-7 md:p-8"
                data-testid="about-expertise-preventive"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33]">Preventive & Screening Care</h3>
                </div>
                <ul className="space-y-3">
                  {expertise.preventive.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-[#1E3F33] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── CLINICAL APPROACH ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="about-clinical-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Treatment Philosophy
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="about-clinical-heading">
                  Clinical Approach
                </h2>
                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Dr. Neha Agarwal follows a patient-first, multidisciplinary treatment approach, ensuring that every case is evaluated carefully before deciding the best treatment plan.
                </p>

                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Each patient is treated with a personalized treatment plan tailored to their condition, stage of disease, and overall health goals.              </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={stagger}
                className="space-y-0"
              >
                {clinicalPrinciples.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    data-testid={`about-clinical-item-${i}`}
                    className="flex items-start gap-4 py-5 border-b border-[#D8DDD9] last:border-0"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#1E3F33]" />
                    </div>
                    <p className="text-base sm:text-lg text-[#1E3F33] font-medium leading-relaxed pt-2">{item.text}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── HOSPITAL AFFILIATIONS ─── */}
        <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="about-hospitals-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Hospital Affiliations
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="about-hospitals-heading">
                Associated Hospitals in Kolkata
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
                Dr. Neha Agarwal is associated with leading cancer care hospitals in Kolkata, ensuring access to advanced infrastructure and multidisciplinary oncology support. These centers provide state-of-the-art facilities for cancer surgery, chemotherapy, HIPEC, and post-operative care.            </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {hospitals.map((h, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`about-hospital-${i}`}
                  className="flex items-center gap-4 bg-[#F9F9F6] rounded-2xl p-6 border border-[#D8DDD9] hover:-translate-y-1 hover:shadow-lg hover:shadow-[#1E3F33]/5 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <span className="text-base font-medium text-[#1E3F33]">{h}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── PHILOSOPHY OF CARE ─── */}
        <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="about-philosophy-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Philosophy
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-3xl" data-testid="about-philosophy-heading">
                Philosophy of Care
              </h2>
              <p className="text-base sm:text-lg text-[#8FA998] mt-4 max-w-2xl leading-relaxed">
                Cancer diagnosis is life-changing. Dr. Neha Agarwal believes that treatment must address not only the disease but also the emotional and psychological well-being of the patient. Her philosophy is based on:
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
            >
              {philosophyPoints.map((point, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`about-philosophy-item-${i}`}
                  className="flex items-start gap-3 bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                >
                  <Heart className="w-4 h-4 text-[#C29F60] mt-0.5 flex-shrink-0" />
                  <p className="text-sm sm:text-base text-[#F9F9F6] font-medium">{point}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
            >
              <blockquote className="font-serif text-2xl sm:text-3xl italic text-[#F9F9F6]/80 leading-snug border-l-4 border-[#C29F60] pl-6 max-w-2xl" data-testid="about-philosophy-quote">
                "Every patient deserves clarity, confidence, and compassionate care during their treatment journey."
              </blockquote>
            </motion.div>
          </div>
        </section>

        {/* ─── PATIENT CARE APPROACH ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="about-patient-care-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Patient Experience
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="about-patient-heading">
                  What Patients Experience
                </h2>
                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Patients often describe their experience with Dr. Neha Agarwal as clear, supportive, and confidence-inspiring.
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={stagger}
                className="space-y-4"
              >
                {patientExperience.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    data-testid={`about-patient-item-${i}`}
                    className="flex items-start gap-4 bg-[#F2EFE9] rounded-2xl p-5"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-[#F9F9F6]" />
                    </div>
                    <p className="text-base text-[#1E3F33] font-medium leading-relaxed pt-1">{item}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── AREAS SERVED ─── */}
        <section className="py-16 md:py-20 bg-[#F2EFE9]" data-testid="about-areas-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Areas Served
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#1E3F33] leading-tight mb-6" data-testid="about-areas-heading">
                Patients From Across Kolkata
              </h2>
              <div className="flex flex-wrap justify-center gap-2" data-testid="about-areas-list">
                {areasServed.map((area, i) => (
                  <span
                    key={i}
                    className="inline-block bg-[#F9F9F6] text-[#1E3F33] px-4 py-2 rounded-full text-sm font-medium border border-[#D8DDD9]"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="about-cta-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl mx-auto mb-4" data-testid="about-cta-heading">
                Book Consultation
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] max-w-lg mx-auto leading-relaxed mb-8">
                If you are looking for expert care for gynecologic cancer or complex gynecologic conditions, early consultation is strongly recommended.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/book-appointment"
                  data-testid="about-cta-book-btn"
                  className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#2C5545] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book Appointment
                </Link>
                <a
                  href="tel:+916291000829"
                  data-testid="about-cta-call-btn"
                  className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                >
                  <Phone className="w-4 h-4" />
                  Get Second Opinion
                </a>
                <a
                  href="https://wa.me/916291000829"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-whatsapp-btn"
                  className="inline-flex items-center gap-2 text-[#4A6359] px-5 py-4 text-sm font-semibold hover:text-[#1E3F33] transition-colors underline underline-offset-4 decoration-[#8FA998]"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp Consultation
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        {/* <footer className="bg-[#1E3F33] py-10" data-testid="about-footer">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-xs text-[#F9F9F6]/30">
                &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
              </p>
              <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Home
              </Link>
            </div>
          </div>
        </footer> */}
      </div>
    </>
  );
}
